import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageContent, Paragraphs } from "@/lib/usePageContent";
import { Button } from "@/components/ui/button";
import { FileText, Search, MessagesSquare, CheckCircle2, ArrowRight, Scale, Unlock, Zap, ShieldCheck, Download } from "lucide-react";

const STEPS = [
  { icon: FileText, title: "Prepare & Submit", text: "Format your manuscript and submit it through our guided submission wizard with authors, abstract and files." },
  { icon: Search, title: "Editorial Screening", text: "An editor checks scope and quality, then assigns independent expert reviewers." },
  { icon: MessagesSquare, title: "Peer Review", text: "Reviewers evaluate your work and provide constructive, confidential feedback." },
  { icon: CheckCircle2, title: "Decision & Publication", text: "On acceptance your article is assigned a DOI and published open access worldwide." },
];

const PERKS = [
  { icon: Unlock, title: "Open Access", text: "Your work is freely readable and citable by anyone, anywhere — maximising reach and impact." },
  { icon: Zap, title: "Rapid, Fair Review", text: "Transparent timelines and constructive feedback from subject-matter experts." },
  { icon: Scale, title: "Author Rights", text: "You retain copyright under a permissive Creative Commons licence." },
  { icon: ShieldCheck, title: "Publication Ethics", text: "We follow COPE guidelines on authorship, plagiarism and conflicts of interest." },
];

export default function ForAuthors() {
  const c = usePageContent("for-authors");
  return (
    <div>
      <PageHeader eyebrow="For Authors" title={c?.title || "For Authors"} subtitle={c?.subtitle} breadcrumb="For Authors" />

      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
          <div>
            <h2 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100">Why submit to us?</h2>
            <div className="mt-4 space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed">
              <Paragraphs text={c?.body} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {PERKS.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/5 dark:bg-primary/20 text-primary dark:text-sky-300 shrink-0"><p.icon className="w-5 h-5" /></span>
                  <div><h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{p.title}</h3><p className="text-sm text-slate-500 mt-0.5">{p.text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -bottom-5 -right-5 w-28 h-28 rounded-2xl border-2 border-amber-400/50 rotate-6 hidden sm:block" />
            <img src="https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Laboratory research" className="relative rounded-2xl shadow-xl w-full h-[340px] object-cover" />
          </div>
        </div>

        <h2 className="font-serif font-bold text-3xl text-slate-900 dark:text-slate-100 text-center mb-10">The submission process</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-6">
              <span className="absolute -top-3 -left-3 grid place-items-center w-8 h-8 rounded-full bg-amber-500 text-white font-bold text-sm">{i + 1}</span>
              <s.icon className="w-7 h-7 text-primary dark:text-sky-400 mb-3" />
              <h3 className="font-serif font-bold text-lg text-slate-900 dark:text-slate-100">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-2">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-[auto_1fr_auto] items-center gap-5 bg-card rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
          <span className="grid place-items-center w-14 h-14 rounded-xl bg-primary/5 dark:bg-primary/20 text-primary dark:text-sky-300 shrink-0">
            <FileText className="w-7 h-7" />
          </span>
          <div>
            <h3 className="font-serif font-bold text-xl text-slate-900 dark:text-slate-100">Manuscript Template (Word)</h3>
            <p className="text-sm text-slate-500 mt-1">Download our official Noventra Academic Press manuscript template — pre-formatted with the title block, abstract, numbered sections, reference style and full formatting guidelines.</p>
          </div>
          <Button asChild size="lg" className="rounded-full gap-2 shrink-0" data-testid="authors-download-template">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manuscript-template`} target="_blank" rel="noopener noreferrer">
              <Download className="w-4 h-4" /> Download .docx
            </a>
          </Button>
        </div>

        <div className="mt-14 relative overflow-hidden rounded-2xl bg-primary text-white p-10 text-center">
          <div className="absolute inset-0 hero-grid opacity-40" />
          <div className="relative">
            <h2 className="font-serif font-bold text-3xl">Ready to submit your manuscript?</h2>
            <p className="mt-3 text-slate-300">Article Processing Charges start at $600. Waivers are available for authors from eligible regions.</p>
            <Button asChild size="lg" className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="authors-submit-cta">
              <Link to="/submit">Start a Submission <ArrowRight className="w-4 h-4 ml-1" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
